import { apiFetch } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Camera, ArrowLeft, ChevronDown, LogOut, Loader2, Volume2,
  ChevronRight, ChevronLeft, RotateCcw, Bookmark
} from "lucide-react";


export default function FlashcardsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await apiFetch(`/api/translations`, {  });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();

      const flashcards = [];
      data.forEach((t) => {
        const lang = t.target_language || "";
        if (t.main_object && t.main_object_translation) {
          flashcards.push({ original: t.main_object, translation: t.main_object_translation, language: lang, type: "object" });
        }
        if (t.translated_objects) {
          t.translated_objects.forEach((obj) => {
            if (obj.name && obj.translation && obj.name.toLowerCase() !== t.main_object?.toLowerCase()) {
              flashcards.push({ original: obj.name, translation: obj.translation, language: lang, type: "object" });
            }
          });
        }
        if (t.original_text && t.translated_text) {
          flashcards.push({ original: t.original_text, translation: t.translated_text, language: lang, type: "text" });
        }
      });

      // Deduplicate
      const seen = new Set();
      const unique = flashcards.filter((c) => {
        const key = `${c.original.toLowerCase()}_${c.translation.toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setCards(unique);
    } catch {
      toast.error("Failed to load flashcards");
    } finally {
      setLoading(false);
    }
  };

  const handleTTS = async (text, language) => {
    if (isPlaying) return;
    setIsPlaying(true);
    try {
      const response = await apiFetch(`/api/translate/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ text, voice: "nova", speed: 1.0, language }),
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      await audio.play();
    } catch {
      setIsPlaying(false);
    }
  };

  const nextCard = () => {
    setFlipped(false);
    setCurrentIndex((prev) => (prev + 1) % cards.length);
  };

  const prevCard = () => {
    setFlipped(false);
    setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
  };

  const card = cards[currentIndex];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")}>
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>LingoLens</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full flex items-center gap-2 hover:bg-slate-50 px-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-bold">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuItem className="text-sm text-slate-500 cursor-default">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} className="text-red-600 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="rounded-full hover:bg-indigo-50" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>Flashcards</h1>
            <p className="text-slate-500 mt-1">{cards.length} cards from your translations</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-3xl bg-indigo-50 flex items-center justify-center mx-auto mb-6">
              <Bookmark className="w-10 h-10 text-indigo-300" />
            </div>
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>No flashcards yet</h3>
            <p className="text-slate-400 mb-6">Translate some images to build your vocabulary deck</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-bold" data-testid="start-translating-btn">
              Start Translating
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Card */}
            <div
              data-testid="flashcard"
              onClick={() => setFlipped(!flipped)}
              className="cursor-pointer select-none"
              style={{ perspective: "1000px" }}
            >
              <div
                className="relative w-full h-72 sm:h-80 transition-transform duration-500"
                style={{
                  transformStyle: "preserve-3d",
                  transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                {/* Front */}
                <div
                  className="absolute inset-0 rounded-3xl bg-white border-2 border-slate-100 shadow-2xl shadow-indigo-100/30 flex flex-col items-center justify-center p-8"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                    {card.type === "object" ? "Object" : "Text"} — tap to flip
                  </span>
                  <p className="text-3xl sm:text-4xl font-black text-slate-900 text-center capitalize" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {card.original}
                  </p>
                  <span className="mt-4 text-sm font-semibold text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full">
                    English
                  </span>
                </div>

                {/* Back */}
                <div
                  className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-700 border-2 border-indigo-400 shadow-2xl flex flex-col items-center justify-center p-8"
                  style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                >
                  <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                    {card.language}
                  </span>
                  <p className="text-3xl sm:text-4xl font-black text-white text-center capitalize" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {card.translation}
                  </p>
                  <Button
                    data-testid="flashcard-tts-btn"
                    onClick={(e) => { e.stopPropagation(); handleTTS(card.translation, card.language); }}
                    disabled={isPlaying}
                    className="mt-6 rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm px-5 py-2 font-bold"
                  >
                    {isPlaying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
                    Listen
                  </Button>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button
                data-testid="prev-card-btn"
                variant="outline"
                onClick={prevCard}
                className="rounded-full w-12 h-12 p-0 border-2 border-slate-200 hover:border-indigo-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <span className="text-sm font-bold text-slate-500 min-w-[80px] text-center">
                {currentIndex + 1} / {cards.length}
              </span>
              <Button
                data-testid="next-card-btn"
                variant="outline"
                onClick={nextCard}
                className="rounded-full w-12 h-12 p-0 border-2 border-slate-200 hover:border-indigo-300"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            <div className="text-center">
              <Button
                data-testid="shuffle-btn"
                variant="ghost"
                onClick={() => {
                  const shuffled = [...cards].sort(() => Math.random() - 0.5);
                  setCards(shuffled);
                  setCurrentIndex(0);
                  setFlipped(false);
                  toast.success("Cards shuffled!");
                }}
                className="rounded-full text-slate-500 hover:text-indigo-600"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Shuffle
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
